import React, { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Icon } from '@web-chat-core/ui-kit';
import { selectMessageInput } from 'state/selectors';
import { useChat } from 'useCase/chat/useChat';
import { MESSAGE_FIELD_ID } from 'consts';
import { useAutoGrowTextarea } from '../hooks/useAutoGrowTextarea';
import { MessageForm, MessageTextarea, SendButton } from './view';

const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);

interface MessageFieldProps {
    onMessageSend: (message: string) => void;
}

export const MessageField = observer(({ onMessageSend }: MessageFieldProps) => {
    const { t } = useTranslation();
    // ref для focus(), чтения selectionStart и установки стилей высоты в useAutoGrowTextarea
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const { setMessageInput } = useChat();
    const message = selectMessageInput();

    useAutoGrowTextarea(textAreaRef, message, {
        maxHeight: 144,
        lineHeight: '1rem',
    });

    useEffect(() => {
        textAreaRef.current?.focus();
    }, []);

    const handleSendMessage = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!message.trim()) return;
            onMessageSend(message);
            setMessageInput('');
            textAreaRef.current?.focus();
        },
        [message, onMessageSend],
    );

    const handleChangeText = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessageInput(event.target.value);
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                if (isMobile || event.ctrlKey || event.shiftKey) {
                    const cursorPosition = textAreaRef.current?.selectionStart ?? 0;
                    setMessageInput(message.slice(0, cursorPosition) + '\n' + message.slice(cursorPosition));
                } else {
                    handleSendMessage(new Event('submit') as unknown as React.FormEvent<HTMLFormElement>);
                }
            }
        },
        [message, handleSendMessage],
    );

    const isSendButtonEnabled = message.trim().length > 0;

    return (
        <MessageForm onSubmit={handleSendMessage}>
            <MessageTextarea
                id={MESSAGE_FIELD_ID}
                placeholder={t('web_call_message...')}
                ref={textAreaRef}
                value={message}
                onChange={handleChangeText}
                onKeyDown={handleKeyDown}
            />
            <SendButton type="submit" disabled={!isSendButtonEnabled}>
                <Icon icon="icSend" colorVariant={isSendButtonEnabled ? 'colored' : 'basic'}></Icon>
            </SendButton>
        </MessageForm>
    );
});

MessageField.displayName = 'MessageField';
